import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ = () => {
  useEffect(() => {
    document.title = 'FAQs - Pin High Golf & Lounge | Frequently Asked Questions';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Frequently asked questions about Pin High Golf & Lounge. Learn about our simulators, reservations, alcohol policy, dress code, and more in Bay Village, Ohio.');
    }

    // Add FAQ Schema markup (must match visible FAQ content)
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why play on a screen when I could be dealing with Ohio weather?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Because here, the sun is always shining, there's no wind to slice your drive, and your ball never actually ends up in Lake Erie! Plus, our \"caddie\" will pour you a beer while you play. Try getting that service on a real course."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need to be good at golf to play here?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely not! Our simulators welcome all skill levels, from \"future PGA Tour pro\" to \"someone who just discovered they own golf clubs.\" We even have fun, non-golf games. Your only prerequisite is the ability to hold a club (and maybe a pint)."
          }
        },
        {
          "@type": "Question",
          "name": "How accurate are these simulators? Is my virtual slice an actual slice?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our state-of-the-art technology is highly accurate. Yes, if you hit a wicked slice on the simulator, you would have hit an even more wicked slice on the actual course. But hey, at least you can drown your sorrows in a locally brewed IPA right here."
          }
        },
        {
          "@type": "Question",
          "name": "Can I bring my own clubs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Please do! Playing with your own gear is always best. Just do us a favor and make sure they're reasonably clean—we want to keep our screens pristine enough to admire your (virtual) hole-in-one. We do have loaner clubs if you forget yours, but they are subject to judging your swing."
          }
        },
        {
          "@type": "Question",
          "name": "How many people can play in one bay?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We recommend up to four golfers for the best pace of play (18 holes takes about 1 hour per person). You can have up to eight people total in a bay, but be warned, more people means more cheerleading, more heckling, and significantly more trips to the bar."
          }
        },
        {
          "@type": "Question",
          "name": "Wait, you really sell alcohol? What are the rules?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We sure do! We have a full selection of beer, wine, and mixed drinks. You must be 21 or older with a valid, non-expired photo ID to purchase or consume alcohol. Absolutely no outside alcohol is permitted."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a reservation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Highly, highly recommended, especially on weekends and during \"bad golf weather\" months. Walk-ins are welcome, but we can't promise a bay, and waiting around without golf in hand is simply tragic."
          }
        },
        {
          "@type": "Question",
          "name": "What is your cancellation policy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can cancel or reschedule up to 24 hours before your booking for a full refund. Less than 24 hours? We'll charge a small cancellation fee—it's hard to find a replacement golfer at the last minute!"
          }
        },
        {
          "@type": "Question",
          "name": "Is there a dress code?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No stuffy country club rules here! We're casual Bay Village. Come as you are, but we do suggest comfortable shoes (no spikes, please—we love our turf mats!) and clothes you can swing in."
          }
        }
      ]
    };

    // Add schema to head
    let script = document.querySelector('#faq-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'faq-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(faqSchema);

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/faq');
    updateSocialMeta({
      title: 'FAQs - Pin High Golf & Lounge | Frequently Asked Questions',
      description: 'Frequently asked questions about Pin High Golf & Lounge. Learn about our simulators, reservations, alcohol policy, dress code, and more in Bay Village, Ohio.',
      path: '/faq',
    });

    // Cleanup on unmount
    return () => {
      const existingScript = document.querySelector('#faq-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const golfSimQuestions = [
    {
      question: "Why play on a screen when I could be dealing with Ohio weather?",
      answer: "Because here, the sun is always shining, there's no wind to slice your drive, and your ball never actually ends up in Lake Erie! Plus, our \"caddie\" will pour you a beer while you play. Try getting that service on a real course."
    },
    {
      question: "Do I need to be good at golf to play here?",
      answer: "Absolutely not! Our simulators welcome all skill levels, from \"future PGA Tour pro\" to \"someone who just discovered they own golf clubs.\" We even have fun, non-golf games. Your only prerequisite is the ability to hold a club (and maybe a pint)."
    },
    {
      question: "How accurate are these simulators? Is my virtual slice an actual slice?",
      answer: "Our state-of-the-art technology is highly accurate. Yes, if you hit a wicked slice on the simulator, you would have hit an even more wicked slice on the actual course. But hey, at least you can drown your sorrows in a locally brewed IPA right here."
    },
    {
      question: "Can I bring my own clubs?",
      answer: "Please do! Playing with your own gear is always best. Just do us a favor and make sure they're reasonably clean—we want to keep our screens pristine enough to admire your (virtual) hole-in-one. We do have loaner clubs if you forget yours, but they are subject to judging your swing."
    },
    {
      question: "How many people can play in one bay?",
      answer: "We recommend up to four golfers for the best pace of play (18 holes takes about 1 hour per person). You can have up to eight people total in a bay, but be warned, more people means more cheerleading, more heckling, and significantly more trips to the bar."
    },
  ];

  const barQuestions = [
    {
      question: "Wait, you really sell alcohol? What are the rules?",
      answer: "We sure do! We have a full selection of beer, wine, and mixed drinks.",
      subItems: [
        {
          title: "The Golden Rule:",
          content: "You must be 21 or older with a valid, non-expired photo ID to purchase or consume alcohol. We check IDs—don't take it personally, it's the law!"
        },
        {
          title: "Hours:",
          content: "We follow Ohio's liquor laws. Alcohol sales generally start early and stop at a very responsible time. Just ask your bartender for the exact closing time!"
        },
        {
          title: "No Outside Beverages:",
          content: "As much as we love your fancy cellar, absolutely no outside alcohol is permitted. Our liquor license is for our liquor."
        }
      ]
    },
  ];

  const reservationQuestions = [
    {
      question: "Do I need a reservation?",
      answer: "Highly, highly recommended, especially on weekends and during \"bad golf weather\" months. Walk-ins are welcome, but we can't promise a bay, and waiting around without golf in hand is simply tragic."
    },
    {
      question: "What is your cancellation policy?",
      answer: "You can cancel or reschedule up to 24 hours before your booking for a full refund. Less than 24 hours? We'll charge a small cancellation fee—it's hard to find a replacement golfer at the last minute!"
    },
    {
      question: "Is there a dress code?",
      answer: "No stuffy country club rules here! We're casual Bay Village. Come as you are, but we do suggest comfortable shoes (no spikes, please—we love our turf mats!) and clothes you can swing in."
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-20 h-20 mx-auto mb-6 text-green-300" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-green-50">
            Everything you need to know about playing at Pin High Golf & Lounge
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* The Golf & Sim Life */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              The Golf & Sim Life
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {golfSimQuestions.map((item, index) => (
                <AccordionItem key={index} value={`golf-${index}`} className="bg-white border border-gray-200 rounded-lg mb-4 px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* The Important Stuff: The Bar */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              The Important Stuff: The Bar
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {barQuestions.map((item, index) => (
                <AccordionItem key={index} value={`bar-${index}`} className="bg-white border border-gray-200 rounded-lg mb-4 px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    <p className="mb-4">{item.answer}</p>
                    {item.subItems && (
                      <ul className="space-y-4">
                        {item.subItems.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <p className="font-semibold text-gray-900 mb-1">{subItem.title}</p>
                            <p className="text-gray-600">{subItem.content}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Reservations & Logistics */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Reservations & Logistics
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {reservationQuestions.map((item, index) => (
                <AccordionItem key={index} value={`reservation-${index}`} className="bg-white border border-gray-200 rounded-lg mb-4 px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl mb-8 text-green-50">
            We're here to help! Contact us directly and we'll get back to you as soon as possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 text-lg px-8 py-6 transition-all duration-300"
            >
              <a href="tel:4404551078">Call (440) 455-1078</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
